import { 
  UserCircle, TrendingUp, Zap, Calendar, Headphones, MousePointer, ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
const features = [
  {
    icon: UserCircle,
    title: "Personalized Assistance",
    description: "Customized financial solutions tailored to your unique profile and requirements.",
  },
  {
    icon: TrendingUp,
    title: "Competitive Interest",
    description: "Multiple financial options with transparent terms and no hidden charges.",
  },
  {
    icon: Zap,
    title: "Fast Processing",
    description: "Quick loan processing system with faster approvals for urgent financial needs.",
  },
  {
    icon: Calendar,
    title: "Flexible Repayment",
    description: "Income-aligned repayment schedules and customized EMI structures.",
  },
  {
    icon: Headphones,
    title: "Dedicated Support",
    description: "End-to-end guidance from documentation to disbursement coordination.",
  },
  {
    icon: MousePointer,
    title: "Easy Online Process",
    description: "Simple digital application journey with real-time status tracking.",
  },
];
const TheCashProEdge = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s cubic-bezier(0.2, 0.9, 0.4, 1.1) forwards;
        }
        .stagger-card {
          opacity: 0;
        }
        .stagger-card:nth-child(1) { animation-delay: 0.05s; }
        .stagger-card:nth-child(2) { animation-delay: 0.1s; }
        .stagger-card:nth-child(3) { animation-delay: 0.15s; }
        .stagger-card:nth-child(4) { animation-delay: 0.2s; }
        .stagger-card:nth-child(5) { animation-delay: 0.25s; }
        .stagger-card:nth-child(6) { animation-delay: 0.3s; }
        .stagger-card.animate-fade-in-up {
          animation-fill-mode: forwards;
        }
      `}</style>

      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6 lg:sticky lg:top-24 animate-on-scroll stagger-card">
              <span className="inline-block px-4 py-1.5 bg-green-600 text-white text-sm font-bold rounded-full shadow-md">
                The CashPro Financial Services Edge
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                Why Smart People
                <span className="text-green-600 block">Choose  CashPro Financial Services</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                At CashPro Financial Services, we don’t just offer loans — we offer thoughtful financial solutions designed around your real needs. Whether you are planning a home, expanding a business, managing personal expenses, or unlocking the value of your property, our team helps you move forward with confidence. With quick processing, minimal documentation, transparent guidance, and customer-first support, we make borrowing simple, reliable, and stress-free.
              </p>
              <div className="flex flex-wrap gap-3 pt-4">
                <span className="bg-gray-100 text-gray-700 px-2 py-2 rounded-full text-sm hover:bg-green-100 hover:text-green-700 transition-colors cursor-default">Personal Loans</span>
                <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-green-100 hover:text-green-700 transition-colors cursor-default">Business Loans</span>
                <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-green-100 hover:text-green-700 transition-colors cursor-default">Education</span>
                <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-green-100 hover:text-green-700 transition-colors cursor-default">Medical</span>
              </div>
              <div className="pt-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:gap-3 group shadow-md hover:shadow-xl"
                >
                  Contact Now
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="group bg-green-700 rounded-xl p-5 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-on-scroll stagger-card"
                  style={{ animationDelay: `${0.05 + idx * 0.05}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-2.5 bg-green-600 rounded-xl group-hover:bg-green-500 transition-all duration-300 group-hover:scale-110">
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1 group-hover:text-green-100 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-green-100 leading-relaxed group-hover:text-white transition-colors">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default TheCashProEdge;